import {
  Alert, AlertIcon, Badge, Box, Button, Flex, FormControl, FormLabel, HStack, Icon,
  Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader,
  ModalOverlay, SimpleGrid, Spinner, Text, Textarea, VStack,
  useDisclosure, useToast,
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { FaCalendarAlt, FaCashRegister, FaHospital, FaInfoCircle, FaUser, FaWallet } from 'react-icons/fa';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useUser } from '@/components/Context/UserContext';
import { useClinic } from '@/components/Context/ClinicaContext';
import { request } from '@/services/fetch';
import useFinancialRefresh, { notifyFinancialUpdate } from '@/hooks/useFinancialRefresh';
import { formatCurrencyPtBrZero } from '@/utils/numberFormat';

const moeda = (centavos) => formatCurrencyPtBrZero(Number(centavos || 0) / 100);
const dataFormatada = (data) => {
  if (!data) return '—';
  const date = new Date(data);
  return Number.isNaN(date.getTime()) ? '—' : format(date, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
};
const identificacao = (conta) => `${conta.nome || 'Conta caixa'} — Agência ${conta.agencia || '—'}${conta.digitoAgencia ? `-${conta.digitoAgencia}` : ''} / Conta ${conta.numeroConta || '—'}${conta.digitoConta ? `-${conta.digitoConta}` : ''}`;

function AberturaCaixaModal({ children }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { selectedClinic } = useClinic();
  const { user } = useUser();
  const toast = useToast();
  const revision = useFinancialRefresh();
  const clinica = selectedClinic?._id;
  const [dados, setDados] = useState(null);
  const [contaCaixaId, setContaCaixaId] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [tentativa, setTentativa] = useState(0);
  const enviando = useRef(false);

  useEffect(() => {
    if (isOpen) { setObservacoes(''); setContaCaixaId(''); }
  }, [isOpen, clinica]);

  useEffect(() => {
    if (!isOpen || !clinica) { setDados(null); return; }
    let cancelado = false;
    setCarregando(true);
    setErro('');
    setDados(null);
    request(`api/financas/caixa/dados-abertura/${clinica}`)
      .then((data) => {
        if (cancelado) return;
        setDados({ ...data, clinica });
        setContaCaixaId((atual) => {
          if (data.aberturaAtual?.contaCaixaId) return String(data.aberturaAtual.contaCaixaId);
          if (data.contasCaixa.some((conta) => String(conta._id) === atual)) return atual;
          return data.contasCaixa.length === 1 ? String(data.contasCaixa[0]._id) : '';
        });
      })
      .catch((error) => { if (!cancelado) setErro(error.message || 'Não foi possível carregar os dados da abertura.'); })
      .finally(() => { if (!cancelado) setCarregando(false); });
    return () => { cancelado = true; };
  }, [isOpen, clinica, revision, tentativa]);

  const dadosAtuais = dados?.clinica === clinica ? dados : null;
  const aberturaAtual = dadosAtuais?.aberturaAtual;
  const ultimoFechamento = dadosAtuais?.ultimoFechamento;
  const contas = dadosAtuais?.contasCaixa || [];
  const conta = contas.find((item) => String(item._id) === contaCaixaId);
  const podeAbrir = !!conta && !!dadosAtuais && !aberturaAtual && !carregando && !salvando;

  const confirmar = async () => {
    if (!podeAbrir || enviando.current) return;
    enviando.current = true;
    setSalvando(true);
    try {
      await request('api/financas/caixa/abrir', {
        method: 'POST',
        body: JSON.stringify({ clinica, contaCaixaId, saldoPrevisto: conta.saldoAbertura, observacoes: observacoes.trim() }),
      });
      notifyFinancialUpdate(clinica);
      toast({ title: 'Caixa aberto com sucesso!', status: 'success', duration: 5000, isClosable: true });
      onClose();
    } catch (error) {
      toast({ title: 'Não foi possível abrir o caixa', description: error.message, status: 'error', duration: 5000, isClosable: true });
      setTentativa((valor) => valor + 1);
    } finally {
      enviando.current = false;
      setSalvando(false);
    }
  };

  return (
    <>
      {typeof children === 'function' ? children({ onOpen }) : children}
      <Modal isOpen={isOpen} onClose={salvando ? () => {} : onClose} size="2xl" closeOnOverlayClick={false} scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent borderRadius="lg" overflow="hidden" w={{ base: 'calc(100vw - 16px)', md: 'calc(100vw - 48px)' }} maxW="1100px" my={{ base: 2, md: 3 }} maxH="calc(100dvh - 32px)">
          <ModalHeader bgGradient="linear(to-r, blue.600, blue.500)" color="white" py={3} pr={12} fontSize="lg">
            <HStack spacing={3}>
              <Flex bg="whiteAlpha.200" borderRadius="full" boxSize={9} align="center" justify="center"><Icon as={FaCashRegister} boxSize={4} /></Flex>
              <Text>Abertura de Caixa</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton color="white" isDisabled={salvando} />
          <ModalBody py={3} px={{ base: 3, md: 4 }}>
            <VStack align="stretch" spacing={2}>
              <SimpleGrid columns={{ base: 1, md: 2 }} gap={3} p={3} borderWidth="1px" borderColor="gray.200" borderRadius="md" bg="gray.50">
                <VStack align="stretch" spacing={1.5} fontSize="sm" minW={0}>
                  {[
                    [FaCalendarAlt, 'Data/Hora:', dataFormatada(aberturaAtual?.data || new Date())],
                    [FaHospital, 'Clínica:', selectedClinic?.razaoSocial || selectedClinic?.nome || 'Selecione uma clínica'],
                    [FaUser, 'Colaborador/Atendente:', aberturaAtual?.usuarioNome || user?.name || user?.nome || '—'],
                  ].map(([icone, label, valor]) => (
                    <Flex key={label} gap={2} align="flex-start" minW={0}>
                      <Icon as={icone} color="blue.500" mt={0.5} flexShrink={0} />
                      <Text fontWeight="semibold" flexShrink={0}>{label}</Text>
                      <Text flex={1} textAlign="right" overflowWrap="anywhere">{valor}</Text>
                    </Flex>
                  ))}
                </VStack>
                <HStack justify="center" spacing={3} borderLeftWidth={{ base: 0, md: '1px' }} borderTopWidth={{ base: '1px', md: 0 }} borderColor="gray.300" pt={{ base: 2, md: 0 }}>
                  <Icon as={FaWallet} color="blue.500" boxSize={6} />
                  <Box textAlign="center">
                    <Text fontWeight="semibold" fontSize="sm">Saldo de abertura</Text>
                    <Text fontSize="2xl" fontWeight="bold" color="green.600">{aberturaAtual ? moeda(aberturaAtual.saldoInformado) : conta ? moeda(conta.saldoAbertura) : '—'}</Text>
                  </Box>
                </HStack>
              </SimpleGrid>
              {carregando && <Flex justify="center"><Spinner aria-label="Carregando abertura" color="teal.500" /></Flex>}
              {erro && <Alert status="error"><AlertIcon /><Box>{erro}<Button size="sm" ml={2} onClick={() => setTentativa((valor) => valor + 1)}>Tentar novamente</Button></Box></Alert>}
              {aberturaAtual && <Alert status="warning"><AlertIcon /><Box>O caixa já está aberto. Feche o caixa atual antes de abrir um novo.<Text fontSize="sm">Conta caixa: {aberturaAtual.contaCaixaNome || 'Não registrada na abertura antiga'}</Text><Text fontSize="sm">Saldo de abertura: {moeda(aberturaAtual.saldoInformado)}</Text></Box></Alert>}
              {dadosAtuais && !aberturaAtual && (
                <>
                  {!contas.length && <Alert status="warning"><AlertIcon />Cadastre uma conta ativa da instituição 000 - Conta caixa para abrir o caixa.</Alert>}
                  {conta && (
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} mt={1} alignItems="stretch">
                      <VStack align="stretch" spacing={3} minW={0}>
                        <Box>
                          <Text fontSize="sm" mb={1}>Conta caixa</Text>
                          <HStack align="flex-start" spacing={2} p={3} borderWidth="1px" borderColor="green.300" borderRadius="md" bg="green.50">
                            <Icon as={FaCashRegister} color="gray.500" mt={1} />
                            <Box minW={0} flex={1}>
                              <Flex justify="space-between" align="center" gap={2} flexWrap="wrap">
                                <Text fontWeight="semibold">{conta.nome || 'Conta caixa'}</Text>
                                <Badge colorScheme="green" fontSize="xs">Conta padrão</Badge>
                              </Flex>
                              <Text fontSize="sm" color="gray.600" mt={1} overflowWrap="anywhere">{identificacao(conta)}</Text>
                              <Text fontSize="xs" color="green.700" mt={1}>000 - Conta caixa · Usada automaticamente</Text>
                            </Box>
                          </HStack>
                        </Box>
                        <FormControl display="flex" flexDirection="column" flex={1} minW={0}>
                          <FormLabel fontSize="sm" mb={1}>Observações</FormLabel>
                          <Textarea value={observacoes} onChange={(event) => setObservacoes(event.target.value)} isDisabled={salvando} placeholder="Observações sobre a abertura do caixa (opcional)" rows={3} minH="84px" flex={1} resize="none" size="sm" borderColor="gray.200" focusBorderColor="blue.400" />
                        </FormControl>
                      </VStack>
                      <VStack align="stretch" spacing={3} minW={0}>
                        <SimpleGrid columns={2} gap={2}>
                          <Box minW={0}>
                            <Text fontSize="sm" mb={1}>Saldo anterior</Text>
                            <Text p={2} borderWidth="1px" borderRadius="md" fontWeight="semibold" data-testid="saldo-anterior">{moeda(conta.saldoAnterior)}</Text>
                            <Text fontSize="xs" color="gray.500" mt={1}>{ultimoFechamento ? `Fechamento: ${dataFormatada(ultimoFechamento.data)}` : 'Sem fechamento anterior.'}</Text>
                          </Box>
                          <Box minW={0}>
                            <Text fontSize="sm" mb={1}>Saldo inicial configurado</Text>
                            <Text p={2} borderWidth="1px" borderRadius="md" fontWeight="semibold" data-testid="saldo-inicial">{moeda(conta.saldoInicial)}</Text>
                            <Text fontSize="xs" color="gray.500" mt={1}>Definido na edição da conta caixa.</Text>
                          </Box>
                        </SimpleGrid>
                        <Box p={3} borderWidth="1px" borderColor="blue.200" borderRadius="md" bg="blue.50" flex={1}>
                          <HStack color="blue.700" mb={2}><Icon as={FaInfoCircle} color="blue.500" /><Text fontWeight="semibold" fontSize="sm">Resumo da operação</Text></HStack>
                          <VStack align="stretch" spacing={1} fontSize="sm">
                            <Flex justify="space-between" gap={2}><Text>Saldo inicial já considerado</Text><Text fontWeight="medium" whiteSpace="nowrap">{moeda(conta.saldoInicialConsiderado)}</Text></Flex>
                            <Flex justify="space-between" gap={2}><Text>Ajuste pendente do saldo inicial</Text><Text fontWeight="medium" whiteSpace="nowrap" data-testid="ajuste-inicial">{moeda(conta.ajusteSaldoInicial)}</Text></Flex>
                            <Flex justify="space-between" gap={2} color="green.700" fontWeight="bold"><Text>Saldo de abertura</Text><Text whiteSpace="nowrap" data-testid="saldo-abertura">{moeda(conta.saldoAbertura)}</Text></Flex>
                          </VStack>
                          <Text fontSize="xs" color="gray.600" mt={2}>Saldo anterior + ajuste pendente, sem somar novamente o saldo inicial já considerado.</Text>
                        </Box>
                      </VStack>
                    </SimpleGrid>
                  )}
                </>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter gap={2} py={2} px={4} flexShrink={0}>
            <Button colorScheme="blue" variant="outline" onClick={onClose} isDisabled={salvando}>Cancelar</Button>
            <Button colorScheme="blue" onClick={confirmar} isLoading={salvando} isDisabled={!podeAbrir}>Confirmar Abertura</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
export default AberturaCaixaModal;
